import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * "As Seen On" carousel host. Layout/motion is CSS-driven; this component:
 *   - wires up prev/next arrow buttons to scroll the track one card at a time
 *   - pauses every <video> when the section leaves the viewport, and again
 *     when the tab loses visibility, then resumes on return
 *   - pauses every <video> entirely when the user prefers reduced motion
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 *
 * @extends Component<Refs>
 */
class AsSeenOn extends Component {
  requiredRefs = ['track'];

  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {(() => void) | null} */
  #onVisibilityChange = null;
  /** @type {(() => void) | null} */
  #onPrev = null;
  /** @type {(() => void) | null} */
  #onNext = null;

  connectedCallback() {
    super.connectedCallback();

    const track = this.refs.track;
    const prev = this.querySelector('[data-aso-prev]');
    const next = this.querySelector('[data-aso-next]');

    if (prev) {
      this.#onPrev = () => this.#scrollByCards(-1);
      prev.addEventListener('click', this.#onPrev);
    }
    if (next) {
      this.#onNext = () => this.#scrollByCards(1);
      next.addEventListener('click', this.#onNext);
    }

    if (prefersReducedMotion()) {
      this.dataset.reducedMotion = 'true';
      this.#pauseAll();
      return;
    }

    if (this.dataset.pauseOffscreen !== 'false') {
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          this.toggleAttribute('data-offscreen', !entry.isIntersecting);
          if (entry.isIntersecting) this.#playAll();
          else this.#pauseAll();
        },
        { threshold: 0 }
      );
      this.#observer.observe(this);

      this.#onVisibilityChange = () => {
        if (document.hidden) this.#pauseAll();
        else if (!this.hasAttribute('data-offscreen')) this.#playAll();
      };
      document.addEventListener('visibilitychange', this.#onVisibilityChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    if (this.#onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    }
    const prev = this.querySelector('[data-aso-prev]');
    const next = this.querySelector('[data-aso-next]');
    if (prev && this.#onPrev) prev.removeEventListener('click', this.#onPrev);
    if (next && this.#onNext) next.removeEventListener('click', this.#onNext);
  }

  #scrollByCards(direction) {
    const track = this.refs.track;
    if (!track) return;
    const item = track.querySelector('.aso__item');
    const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
    const step = (item ? item.getBoundingClientRect().width : track.clientWidth / 2) + gap;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  #pauseAll() {
    for (const video of this.querySelectorAll('video')) {
      video.pause();
    }
  }

  #playAll() {
    for (const video of this.querySelectorAll('video')) {
      const result = video.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }
  }
}

if (!customElements.get('as-seen-on')) {
  customElements.define('as-seen-on', AsSeenOn);
}
